/**
 * dynamoPipeline — a self-sustaining magnetic dynamo.
 *
 * A geodynamo is the process by which a rotating, convecting,
 * electrically conducting fluid generates a magnetic field. The
 * Earth's magnetic field is produced by this mechanism in the
 * liquid iron outer core.
 *
 * Innovations:
 *   - We render a circular convection cell with rising/falling
 *     plumes (modeled as advected blobs).
 *   - Magnetic field lines are drawn as twisted curves emerging
 *     from the cell, with a dipole-like structure outside the
 *     cell.
 *   - The polarity of the field periodically reverses (a
 *     "magnetic reversal"), shown by the field lines flipping
 *     direction.
 *   - The convection plumes rotate with the cell.
 *   - Audio: bass drives the rotation, mid controls the reversal
 *     rate, high drives the field line brightness.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface DynamoPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.02, 0.04, 0.08), vec3(0.005, 0.01, 0.02), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // The convection cell is a circle at the center.
  vec2 cellCenter = vec2(0.0, 0.0);
  float cellR = 0.30;
  float cellD = length(p - cellCenter);
  // Polarity of the magnetic field.
  float polarity = sign(sin(uTime * 0.5));
  // Cell body: a warm gradient.
  if (cellD < cellR) {
    // Distance from center, normalized.
    float n = cellD / cellR;
    // Convection plumes: rotating FBM.
    float ang = atan(p.y - cellCenter.y, p.x - cellCenter.x);
    float rot = uTime * 0.5;
    float a = mod(ang + rot, 6.2831 / 6.0) * 6.0 / 6.2831;
    // Plume: bright at certain angles.
    float plume = 0.5 + 0.5 * sin(a * 6.2831 * 1.5 + uTime * 2.0);
    // Hot center, cool edge.
    vec3 cellColor = mix(
      vec3(1.0, 0.95, 0.6),
      vec3(0.95, 0.30, 0.10),
      n
    );
    cellColor *= 0.7 + 0.6 * plume;
    col = mix(col, cellColor, 0.85);
  }
  // Cell edge.
  float cellEdge = 1.0 - smoothstep(0.0, 0.005, abs(cellD - cellR));
  col = mix(col, vec3(0.95, 0.7, 0.4), cellEdge * 0.8);
  // Cell halo (corona).
  col += vec3(0.95, 0.5, 0.2) * exp(-abs(cellD - cellR) * 15.0) * 0.3;

  // Rotation axis (vertical line).
  float axisMask = 1.0 - smoothstep(0.0, 0.004, abs(p.x));
  axisMask *= step(abs(p.y), 0.55);
  col = mix(col, vec3(0.95, 0.85, 0.65), axisMask * 0.4);

  // Magnetic dipole field lines: 8 lines emerging from the cell.
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float phi = fi * 0.7854;
    // A field line: a curve from one pole to the other.
    // Use a parametric curve.
    float minDist = 1e9;
    for (int k = 0; k < 50; k++) {
      float fk = float(k);
      float t = fk / 49.0;
      // Magnetic latitude: from 0 (north pole) to pi (south pole).
      float lat = t * 3.14159;
      // r is the radial distance.
      float r = 0.40 * sin(lat) * sin(lat) * polarity;
      // Azimuth.
      float az = phi + (t - 0.5) * 1.5 * polarity;
      vec2 linePt = vec2(r * cos(az), r * sin(lat) * 0.7);
      float d = length(p - linePt);
      minDist = min(minDist, d);
    }
    float lineMask = 1.0 - smoothstep(0.0, 0.004, minDist);
    vec3 lineColor = vec3(0.40, 0.85, 1.0);
    col += lineColor * lineMask * 0.7;
  }

  // A compass needle showing polarity.
  vec2 compassPos = vec2(0.0, 0.55);
  float compassD = length(p - compassPos);
  float compassShape = 1.0 - smoothstep(0.020, 0.025, compassD);
  col = mix(col, vec3(0.95, 0.7, 0.4), compassShape * 0.7);
  // The needle.
  vec2 needleEnd = compassPos + vec2(0.0, 0.03 * polarity);
  vec2 needleDir = needleEnd - compassPos;
  float nd = 1e9;
  for (int k = 0; k < 10; k++) {
    float fk = float(k) / 9.0;
    vec2 pt = compassPos + needleDir * fk;
    float d2 = length(p - pt);
    nd = min(nd, d2);
  }
  float needleMask = 1.0 - smoothstep(0.0, 0.003, nd);
  col += vec3(0.95, 0.95, 0.7) * needleMask * 1.5;

  // Polarity indicator at the bottom.
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float dotX = -0.04 + fi * 0.04;
    float dotY = -0.55;
    float dotD = length(p - vec2(dotX, dotY));
    float dotMask = 1.0 - smoothstep(0.0, 0.005, dotD);
    bool on = (polarity > 0.0 && (i == 0 || i == 1)) || (polarity < 0.0 && i == 2);
    if (on) {
      col += vec3(0.95, 0.55, 0.20) * dotMask * 0.8;
    } else {
      col += vec3(0.5, 0.5, 0.5) * dotMask * 0.3;
    }
  }

  // Stars in the background.
  for (int i = 0; i < 20; i++) {
    float fi = float(i);
    vec2 sp = vec2(
      sin(fi * 1.7 + 0.3) * 1.0,
      cos(fi * 2.1 + 1.7) * 0.8
    );
    float d = length(p - sp);
    col += vec3(0.85, 0.9, 1.0) * exp(-d * 200.0) * 0.3;
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeDynamoBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeDynamoRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createDynamoPipeline(gl: WebGL2RenderingContext): DynamoPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawDynamo(
  gl: WebGL2RenderingContext,
  pipe: DynamoPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
